import type { PendingComments } from '@/data/v2/pr/pending-comments-types';
import type {
  PrV2CommentResponse,
  PrV2DiscussionResponse,
  PrV2ReviewResponse,
  PrV2ReviewThread,
} from '@/lib/pr-v2/types';

import type { ReviewTypeValue } from './review-submit-types';

import { CommentSubjectType } from '../pending-comments-types';

import { commentSideToGithubReviewSide } from '@/lib/pr-v2/comment-side';
import { TIMELINE_ITEM_TYPE } from '@/lib/pr-v2/timeline-types';
import { asRecord } from '@/lib/pr-v2/utils/as-record';
import { ReviewerState, ReviewType } from '@/types/github';

export { commentSideToGithubReviewSide } from '@/lib/pr-v2/comment-side';

let optimisticSequence = 0;

export function nextOptimisticId(prefix: string): string {
  optimisticSequence += 1;
  return `optimistic-${prefix}-${Date.now()}-${optimisticSequence}`;
}

export function normalizeReviewState(reviewType: ReviewTypeValue): string {
  if (reviewType === ReviewType.APPROVE) {
    return ReviewerState.APPROVED;
  }

  if (reviewType === ReviewType.REQUEST_CHANGES) {
    return ReviewerState.CHANGES_REQUESTED;
  }

  return ReviewerState.COMMENTED;
}

export function normalizeReviewBody(
  comment: string,
  reviewType: ReviewTypeValue,
  pendingCommentsCount: number,
): string {
  const trimmed = comment.trim();

  if (trimmed.length > 0) {
    return comment;
  }

  if (reviewType === ReviewType.APPROVE) {
    return '';
  }

  return pendingCommentsCount > 0 ? 'See comments' : comment;
}

export function buildOptimisticReview(args: {
  reviewType: ReviewTypeValue;
  body: string;
  userLogin: string;
  userAvatarUrl: string | null;
  commitId: string | null;
  timestamp: string;
}): PrV2ReviewResponse {
  const optimisticId = nextOptimisticId('review');
  const state = normalizeReviewState(args.reviewType);

  return {
    id: optimisticId,
    githubReviewId: optimisticId,
    githubReviewNodeId: optimisticId,
    state,
    body: args.body,
    submittedAt: args.timestamp,
    userLogin: args.userLogin,
    userAvatarUrl: args.userAvatarUrl,
    commitId: args.commitId,
    raw: {
      node_id: optimisticId,
      is_optimistic: true,
    },
  };
}

export function buildOptimisticReviewTimelineItem(args: {
  optimisticReview: PrV2ReviewResponse;
  timestamp: string;
}): PrV2DiscussionResponse['timelineItems'][number] {
  return {
    id: args.optimisticReview.githubReviewId,
    type: TIMELINE_ITEM_TYPE.Review,
    timestamp: args.timestamp,
    content: args.optimisticReview.body ?? '',
    node_id: args.optimisticReview.githubReviewNodeId ?? args.optimisticReview.id,
    user: {
      login: args.optimisticReview.userLogin ?? 'you',
      avatar_url: args.optimisticReview.userAvatarUrl ?? '',
      html_url: '',
    },
    url: '',
    created_at: args.timestamp,
    updated_at: args.timestamp,
    state: args.optimisticReview.state ?? ReviewerState.COMMENTED,
    body: args.optimisticReview.body ?? '',
    pull_request_url: '',
    _links: {
      html: { href: '' },
      pull_request: { href: '' },
    },
    commit_id: args.optimisticReview.commitId ?? '',
    isPending: true,
  } as PrV2DiscussionResponse['timelineItems'][number];
}

export function buildOptimisticPromotedComment(args: {
  pendingComment: PendingComments;
  inReplyToId: string | null;
  githubReviewId: string;
  userLogin: string;
  userAvatarUrl: string | null;
  timestamp: string;
  localTransitionKey: string;
}): PrV2CommentResponse {
  const optimisticId = nextOptimisticId('review_comment');
  const subjectType = args.pendingComment.subject_type ?? CommentSubjectType.line;
  const side = commentSideToGithubReviewSide(args.pendingComment.side);
  const startSide = args.pendingComment.start_side
    ? commentSideToGithubReviewSide(args.pendingComment.start_side)
    : undefined;

  return {
    id: optimisticId,
    githubCommentId: optimisticId,
    commentType: 'review_comment',
    githubReviewId: args.githubReviewId,
    body: args.pendingComment.content,
    path: args.pendingComment.filename,
    line: subjectType === CommentSubjectType.line ? args.pendingComment.lineNumber : null,
    position: null,
    inReplyToId: args.inReplyToId,
    userLogin: args.userLogin,
    userAvatarUrl: args.userAvatarUrl,
    createdAtGithub: args.timestamp,
    updatedAtGithub: args.timestamp,
    raw: {
      node_id: optimisticId,
      subject_type: subjectType,
      side,
      ...(subjectType === CommentSubjectType.line ? { line: args.pendingComment.lineNumber } : {}),
      ...(args.pendingComment.start_line ? { start_line: args.pendingComment.start_line } : {}),
      ...(startSide ? { start_side: startSide } : {}),
      // Preserve the queued-comment identity through review submit so the diff can
      // swap sources without tearing down the rendered thread.
      local_transition_key: args.localTransitionKey,
      is_optimistic: true,
    },
  };
}

export function buildOptimisticReviewCommentTimelineItem(
  comment: PrV2CommentResponse,
): PrV2DiscussionResponse['timelineItems'][number] {
  const parentId = comment.inReplyToId ?? undefined;
  const parentNumericId =
    parentId && /^\d+$/.test(parentId) ? Number.parseInt(parentId, 10) : undefined;
  const parentReviewId = comment.githubReviewId ?? undefined;
  const parentReviewNumericId =
    parentReviewId && /^\d+$/.test(parentReviewId)
      ? Number.parseInt(parentReviewId, 10)
      : undefined;

  return {
    id: comment.githubCommentId,
    type: TIMELINE_ITEM_TYPE.ReviewComment,
    timestamp: comment.createdAtGithub ?? new Date().toISOString(),
    content: comment.body ?? '',
    node_id:
      typeof comment.raw === 'object' &&
      comment.raw &&
      'node_id' in comment.raw &&
      typeof (comment.raw as { node_id?: unknown }).node_id === 'string'
        ? (comment.raw as { node_id: string }).node_id
        : comment.id,
    user: {
      login: comment.userLogin ?? 'you',
      avatar_url: comment.userAvatarUrl ?? '',
      html_url: '',
    },
    ...(parentId ? { parentId } : {}),
    ...(parentNumericId !== undefined ? { in_reply_to_id: parentNumericId } : {}),
    ...(parentReviewId ? { parentReviewId } : {}),
    ...(parentReviewNumericId !== undefined
      ? { pull_request_review_id: parentReviewNumericId }
      : {}),
    path: comment.path ?? undefined,
    line: comment.line ?? undefined,
    position: comment.position ?? undefined,
  } as PrV2DiscussionResponse['timelineItems'][number];
}

export function buildOptimisticThreadCommentNode(
  comment: PrV2CommentResponse,
): PrV2ReviewThread['comments']['nodes'][number] {
  const raw = asRecord(comment.raw) ?? {};

  return {
    id: typeof raw.node_id === 'string' && raw.node_id.length > 0 ? raw.node_id : comment.id,
    path: comment.path,
    line: comment.line,
    originalLine: typeof raw.line === 'number' ? raw.line : comment.line,
    startLine: typeof raw.start_line === 'number' ? raw.start_line : null,
    originalStartLine: typeof raw.start_line === 'number' ? raw.start_line : null,
  };
}

export function buildOptimisticReviewThread(comment: PrV2CommentResponse): PrV2ReviewThread {
  const raw = asRecord(comment.raw) ?? {};

  return {
    id: nextOptimisticId('thread'),
    isResolved: false,
    isOutdated: false,
    path: comment.path,
    line: comment.line,
    originalLine: typeof raw.line === 'number' ? raw.line : comment.line,
    startLine: typeof raw.start_line === 'number' ? raw.start_line : null,
    originalStartLine: typeof raw.start_line === 'number' ? raw.start_line : null,
    diffSide: raw.side === 'LEFT' || raw.side === 'RIGHT' ? raw.side : null,
    startDiffSide: raw.start_side === 'LEFT' || raw.start_side === 'RIGHT' ? raw.start_side : null,
    comments: {
      nodes: [buildOptimisticThreadCommentNode(comment)],
    },
  };
}
